import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ImportRecordsModal from './ImportRecordsModal';
import ImportLogsModal from './ImportLogsModal';
import AddAttendance from './AddAttendance';
import EditAttendance from './EditAttendance';

const Attendance = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attStatuses, setAttStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showImportLogsModal, setShowImportLogsModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE;

  // Permission state
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    can_import: false,
    can_export: false,
    loaded: false
  });

  // Fetch user permissions for Attendance module
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_import: false,
          can_export: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const attendancePermissions = response.data.permissions.find(
          p => p.module_name === 'Attendance' || p.module_name === 'Attendance Management'
        );

        if (attendancePermissions) {
          setUserPermissions({
            can_view: Boolean(attendancePermissions.can_view),
            can_add: Boolean(attendancePermissions.can_add),
            can_edit: Boolean(attendancePermissions.can_edit),
            can_delete: Boolean(attendancePermissions.can_delete),
            can_import: Boolean(attendancePermissions.can_import || attendancePermissions.can_add),
            can_export: Boolean(attendancePermissions.can_export),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view: true,
            can_add: true,
            can_edit: true,
            can_delete: true,
            can_import: true,
            can_export: true,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: true,
          can_add: true,
          can_edit: true,
          can_delete: true,
          can_import: true,
          can_export: true,
          loaded: true
        });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        can_view: true,
        can_add: true,
        can_edit: true,
        can_delete: true,
        can_import: true,
        can_export: true,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchAttendanceData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/attendance.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAttendanceRecords(response.data.attendance);
      }
    } catch (error) {
      setError('Failed to fetch attendance records');
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/employees.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setEmployees(response.data.employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, [API_BASE]);

  const fetchAttStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_att_statuses.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAttStatuses(response.data.statuses);
      }
    } catch (error) {
      console.error('Error fetching attendance statuses:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      if (userPermissions.can_view) {
        await Promise.all([
          fetchAttendanceData(),
          fetchEmployees(),
          fetchAttStatuses()
        ]);
      }
    };

    initializeData();
  }, [fetchUserPermissions, userPermissions.can_view, fetchAttendanceData, fetchEmployees, fetchAttStatuses]);

  // Permission check functions
  const canViewAttendance = () => userPermissions.can_view;
  const canAddAttendance = () => userPermissions.can_add;
  const canEditAttendance = () => userPermissions.can_edit;
  const canDeleteAttendance = () => userPermissions.can_delete;
  const canImportAttendance = () => userPermissions.can_import;
  const canExportAttendance = () => userPermissions.can_export;

  // Function to format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return dateString;
    }
  };

  // Handle records import
  const handleRecordsImport = async (csvData, setProgress) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}imports/import_attendance.php`,
        { records: csvData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Import completed! ${response.data.success_count} records imported successfully, ${response.data.error_count} failed.`);
        if (response.data.errors && response.data.errors.length > 0) {
          console.error('Import errors:', response.data.errors);
        }
        fetchAttendanceData();
        return true;
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (error) {
      throw new Error('Import failed: ' + (error.response?.data?.error || error.message));
    }
  };

  // Handle logs import
  const handleLogsImport = async (logsCsvData, setProgress) => {
    try {
      const token = localStorage.getItem('authToken');

      const validatedLogs = logsCsvData.map(log => ({
        emp_code: log.emp_code || '',
        device_sn: log.device_sn || '',
        punch_time: log.punch_time || '',
        punch_type: log.punch_type || 0,
        created_at: new Date().toISOString()
      }));

      const response = await axios.post(`${API_BASE}imports/import_attendance_logs.php`,
        { logs: validatedLogs },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Import completed! ${response.data.success_count} logs imported successfully, ${response.data.error_count} failed.`);
        if (response.data.errors && response.data.errors.length > 0) {
          console.error('Import errors:', response.data.errors);
          setError(`Some logs failed to import: ${JSON.stringify(response.data.errors.slice(0, 3))}`);
        }
        fetchAttendanceData();
        return true;
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (error) {
      throw new Error('Import failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (attendanceId) => {
    if (!canDeleteAttendance()) {
      alert('You do not have permission to delete attendance records');
      return;
    }

    if (window.confirm('Are you sure you want to delete this attendance record?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_attendance.php`,
          { attendance_id: attendanceId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchAttendanceData();
        } else {
          setError(response.data.error || 'Failed to delete attendance record');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete attendance record');
      }
    }
  };

  const handleEdit = (record) => {
    if (!canEditAttendance()) {
      alert('You do not have permission to edit attendance records');
      return;
    }
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  // Navigation handlers
  const goToDashboard = () => navigate('/dashboard');
  const goToAttStatus = () => navigate('/att-status');
  const goToHome = () => navigate('/');
  const goToAttendanceView = () => navigate('/attendance-view');
  const handleLogout = () => logout();

  // Filter attendance records
  const filteredRecords = attendanceRecords.filter(record => {
    const employee = employees.find(emp => emp.emp_id === record.emp_id);
    const matchesSearch =
      employee?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee?.emp_code?.toLowerCase().includes(searchTerm.toLowerCase());

    const recordDate = formatDateForInput(record.att_date);
    const matchesDate = !dateFilter || recordDate === dateFilter;

    const status = attStatuses.find(s => s.status_id === record.status_id);
    const matchesStatus = statusFilter === 'All' || status?.status_name === statusFilter;

    return matchesSearch && matchesDate && matchesStatus;
  });

  // Show loading while permissions are being fetched
  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have view permission, show access denied
  if (!canViewAttendance()) {
    return (
      <div className="dashboard-container">
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
          <div className="container-fluid">
            <Link to="/" className="navbar-brand mb-0 h1 text-decoration-none">
              <i className="bi bi-building me-2"></i>
              HRMS Dashboard
            </Link>

            <div className="d-none d-lg-flex align-items-center">
              <div className="navbar-nav me-3">
                <Link to="/" className="nav-link text-light">
                  <i className="bi bi-house me-1"></i>
                  Home
                </Link>
                <Link to="/employees" className="nav-link text-light">
                  <i className="bi bi-people me-1"></i>
                  Employees
                </Link>
                <Link to="/attendance" className="nav-link text-light active">
                  <i className="bi bi-calendar-check me-1"></i>
                  Attendance
                </Link>
                <Link to="/menu" className="nav-link text-light">
                  <i className="bi bi-list me-1"></i>
                  Menu
                </Link>
              </div>

              <div className="dropdown">
                <button
                  className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-person-circle me-2"></i>
                  {user?.username || 'User'}
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link className="dropdown-item" to="/company-settings">
                      <i className="bi bi-person me-2"></i>Profile
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/admin-panel">
                      <i className="bi bi-gear me-2"></i>Settings
                    </Link>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item text-danger" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div className="d-lg-none">
              <Link to="/menu" className="btn btn-outline-light btn-sm">
                <i className="bi bi-list"></i>
              </Link>
            </div>
          </div>
        </nav>
        <div className="container-fluid mt-4">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body text-center py-5">
                  <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                  <h3 className="mt-3 text-warning">Access Denied</h3>
                  <p className="text-muted">You do not have permission to view attendance records.</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Back to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Enhanced Navigation Bar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
        <div className="container-fluid">
          <Link to="/" className="navbar-brand mb-0 h1 text-decoration-none">
            <i className="bi bi-building me-2"></i>
            HRMS Dashboard
          </Link>

          <div className="d-none d-lg-flex align-items-center">
            <div className="navbar-nav me-3">
              <Link to="/" className="nav-link text-light">
                <i className="bi bi-house me-1"></i>
                Home
              </Link>
              <Link to="/employees" className="nav-link text-light">
                <i className="bi bi-people me-1"></i>
                Employees
              </Link>
              <Link to="/attendance" className="nav-link text-light active">
                <i className="bi bi-calendar-check me-1"></i>
                Attendance
              </Link>
              <Link to="/menu" className="nav-link text-light">
                <i className="bi bi-list me-1"></i>
                Menu
              </Link>
            </div>

            <div className="dropdown">
              <button
                className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-person-circle me-2"></i>
                {user?.username || 'User'}
                {!canAddAttendance() && !canEditAttendance() && !canDeleteAttendance() &&
                  !canImportAttendance() && !canExportAttendance() && (
                    <span className="badge bg-warning ms-2">View Only</span>
                  )}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <Link className="dropdown-item" to="/company-settings">
                    <i className="bi bi-person me-2"></i>Profile
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/admin-panel">
                    <i className="bi bi-gear me-2"></i>Settings
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="d-lg-none">
            <Link to="/menu" className="btn btn-outline-light btn-sm">
              <i className="bi bi-list"></i>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-fluid mt-4" style={{ paddingBottom: '80px' }}>
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body py-4">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="btn-group me-3" role="group">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToHome}
                          title="Go to Home"
                        >
                          <i className="bi bi-house"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToDashboard}
                          title="Go to Dashboard"
                        >
                          <i className="bi bi-speedometer2"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToAttStatus}
                          title="Go to Attendance Status"
                        >
                          <i className="bi bi-list-check"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={goToAttendanceView}
                          title="Go to Attendance View"
                        >
                          <i className="bi bi-calendar-range"></i>
                        </button>
                      </div>
                      <div>
                        <h2 className="h4 mb-1 fw-bold text-dark">
                          <i className="bi bi-calendar-check me-2 text-primary"></i>
                          Attendance Management
                          {!canAddAttendance() && !canEditAttendance() && !canDeleteAttendance() &&
                            !canImportAttendance() && !canExportAttendance() && (
                              <span className="badge bg-warning ms-2">View Only</span>
                            )}
                        </h2>
                        <p className="text-muted mb-0">
                          Manage employee attendance records
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="d-flex justify-content-end align-items-center">
                      <div className="btn-group me-2" role="group">
                        <button
                          className="btn btn-outline-info"
                          onClick={goToAttStatus}
                          title="Manage Attendance Status"
                        >
                          <i className="bi bi-list-check me-2"></i>
                          Status Types
                        </button>
                      </div>
                      {canImportAttendance() && (
                        <div className="btn-group me-2" role="group">
                          <button
                            className="btn btn-success me-2"
                            onClick={() => setShowImportModal(true)}
                          >
                            <i className="bi bi-upload me-2"></i>
                            Import Records
                          </button>
                          <button
                            className="btn btn-warning"
                            onClick={() => setShowImportLogsModal(true)}
                            title="Import raw attendance logs from biometric devices"
                          >
                            <i className="bi bi-device-hdd me-2"></i>
                            Import Logs
                          </button>
                        </div>
                      )}
                      {canAddAttendance() && (
                        <button
                          className="btn btn-primary"
                          onClick={() => setShowAddModal(true)}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Add Record
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card bg-light border-0">
              <div className="card-body py-3">
                <div className="row text-center">
                  <div className="col-md-2 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                      onClick={goToHome}
                    >
                      <i className="bi bi-house me-2"></i>
                      Home
                    </button>
                  </div>
                  <div className="col-md-2 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                      onClick={goToDashboard}
                    >
                      <i className="bi bi-speedometer2 me-2"></i>
                      Dashboard
                    </button>
                  </div>
                  <div className="col-md-2 mb-2 mb-md-0">
                    <Link
                      to="/attendance"
                      className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-calendar-check me-2"></i>
                      Attendance
                    </Link>
                  </div>
                  <div className="col-md-2 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                      onClick={goToAttendanceView}
                    >
                      <i className="bi bi-calendar-range me-2"></i>
                      View
                    </button>
                  </div>
                  <div className="col-md-2 mb-2 mb-md-0">
                    <button
                      className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                      onClick={goToAttStatus}
                    >
                      <i className="bi bi-list-check me-2"></i>
                      Status
                    </button>
                  </div>
                  <div className="col-md-2">
                    <Link
                      to="/menu"
                      className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-grid-3x3-gap me-2"></i>
                      Menu
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters and Search */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="row g-3 align-items-center">
                  <div className="col-md-3">
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <input
                      type="date"
                      className="form-control"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="All">All Status</option>
                      {attStatuses.filter(s => s.is_active).map(status => (
                        <option key={status.status_id} value={status.status_name}>
                          {status.status_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 text-end">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={fetchAttendanceData}
                      title="Refresh"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                    {canExportAttendance() && (
                      <button
                        className="btn btn-outline-success ms-2"
                        onClick={() => alert('Export functionality coming soon!')}
                        title="Export Attendance"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>{error}</div>
                <button
                  type="button"
                  className="btn-close ms-auto"
                  onClick={() => setError('')}
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* Attendance Records Table */}
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-0">
                {filteredRecords.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-calendar-check display-1 text-muted"></i>
                    <h4 className="mt-3 text-muted">No attendance records found</h4>
                    <p className="text-muted">
                      {attendanceRecords.length === 0
                        ? "Get started by adding your first attendance record or importing from CSV."
                        : "No records match your search criteria."
                      }
                    </p>
                    <div className="mt-3">
                      {canAddAttendance() && (
                        <button
                          className="btn btn-primary me-2"
                          onClick={() => setShowAddModal(true)}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Add First Record
                        </button>
                      )}
                      {canImportAttendance() && (
                        <div>
                          <button
                            className="btn btn-success me-2"
                            onClick={() => setShowImportModal(true)}
                          >
                            <i className="bi bi-upload me-2"></i>
                            Import Records
                          </button>
                          <button
                            className="btn btn-warning"
                            onClick={() => setShowImportLogsModal(true)}
                          >
                            <i className="bi bi-device-hdd me-2"></i>
                            Import Device Logs
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4">Employee</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Attendance Value</th>
                          <th>In Time</th>
                          <th>Out Time</th>
                          <th>Work Hours</th>
                          <th>Punch Source</th>
                          <th>Verified</th>
                          <th>Remarks</th>
                          {(canEditAttendance() || canDeleteAttendance()) && (
                            <th className="text-end pe-4">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecords.map((record) => {
                          const employee = employees.find(emp => emp.emp_id === record.emp_id);
                          const status = attStatuses.find(s => s.status_id === record.status_id);

                          return (
                            <tr key={record.attendance_id}>
                              <td className="ps-4">
                                {employee ? (
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="employee-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                      style={{ width: '40px', height: '40px' }}
                                    >
                                      {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                    </div>
                                    <div>
                                      <div className="fw-semibold">
                                        {employee.first_name} {employee.last_name}
                                      </div>
                                      <small className="text-muted">{employee.emp_code}</small>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted">Employee not found</span>
                                )}
                              </td>
                              <td>
                                <span className="fw-semibold">
                                  {formatDate(record.att_date)}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${status ? 'bg-info' : 'bg-secondary'
                                  }`}>
                                  {status ? status.status_name : 'No Status'}
                                </span>
                              </td>
                              <td>
                                <span className="fw-semibold">
                                  {record.att_value}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {record.in_time || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {record.out_time || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="fw-semibold">
                                  {record.work_hours || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {record.punch_source ? (
                                  <span className={`badge ${
                                    record.punch_source === 'MOBILE' ? 'bg-primary' :
                                    record.punch_source === 'DEVICE' ? 'bg-success' :
                                    'bg-secondary'
                                  }`}>
                                    <i className={`bi ${
                                      record.punch_source === 'MOBILE' ? 'bi-phone' :
                                      record.punch_source === 'DEVICE' ? 'bi-hdd' :
                                      'bi-pencil'
                                    } me-1`}></i>
                                    {record.punch_source}
                                  </span>
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  <span className={`badge ${
                                    record.in_verified ? 'bg-success' : 'bg-light text-muted border'
                                  }`} style={{ fontSize: '0.7rem' }}>
                                    <i className={`bi ${
                                      record.in_verified ? 'bi-check-circle' : 'bi-dash-circle'
                                    } me-1`}></i>
                                    In {record.in_verified ? 'Verified' : 'Unverified'}
                                  </span>
                                  <span className={`badge ${
                                    record.out_verified ? 'bg-success' : 'bg-light text-muted border'
                                  }`} style={{ fontSize: '0.7rem' }}>
                                    <i className={`bi ${
                                      record.out_verified ? 'bi-check-circle' : 'bi-dash-circle'
                                    } me-1`}></i>
                                    Out {record.out_verified ? 'Verified' : 'Unverified'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {record.remarks || 'No remarks'}
                                </small>
                              </td>
                              {(canEditAttendance() || canDeleteAttendance()) && (
                                <td className="text-end pe-4">
                                  <div className="btn-group" role="group">
                                    {canEditAttendance() && (
                                      <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => handleEdit(record)}
                                        title="Edit"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                    )}
                                    {canDeleteAttendance() && (
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => handleDelete(record.attendance_id)}
                                        title="Delete"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Footer */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card bg-light border-0">
              <div className="card-body py-3">
                <div className="row text-center">
                  <div className="col-md-3">
                    <div className="fw-bold text-primary fs-4">{filteredRecords.length}</div>
                    <small className="text-muted">Showing Records</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-success fs-4">{attendanceRecords.length}</div>
                    <small className="text-muted">Total Records</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-info fs-4">
                      {[...new Set(attendanceRecords.map(r => r.emp_id))].length}
                    </div>
                    <small className="text-muted">Unique Employees</small>
                  </div>
                  <div className="col-md-3">
                    <div className="fw-bold text-warning fs-4">
                      {[...new Set(attendanceRecords.map(r => r.att_date))].length}
                    </div>
                    <small className="text-muted">Unique Dates</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation Footer */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0">
              <div className="card-body text-center py-3">
                <div className="btn-group" role="group">
                  <button
                    className="btn btn-outline-primary"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Back to Home
                  </button>
                  <button
                    className="btn btn-outline-success"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Back to Dashboard
                  </button>
                  <button
                    className="btn btn-outline-warning"
                    onClick={goToAttStatus}
                  >
                    <i className="bi bi-list-check me-2"></i>
                    Manage Status Types
                  </button>
                  <button
                    className="btn btn-outline-info"
                    onClick={goToAttendanceView}
                  >
                    <i className="bi bi-calendar-range me-2"></i>
                    Attendance View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modals */}
      <ImportRecordsModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImport={handleRecordsImport}
        canImportAttendance={canImportAttendance}
        error={error}
        setError={setError}
      />

      <ImportLogsModal
        show={showImportLogsModal}
        onHide={() => setShowImportLogsModal(false)}
        onImport={handleLogsImport}
        canImportAttendance={canImportAttendance}
        error={error}
        setError={setError}
      />

      {/* Add Record Modal */}
      {showAddModal && canAddAttendance() && (
        <AddAttendance
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onAttendanceAdded={fetchAttendanceData}
        />
      )}

      {/* Edit Record Modal */}
      {showEditModal && selectedRecord && canEditAttendance() && (
        <EditAttendance
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          record={selectedRecord}
          onAttendanceUpdated={fetchAttendanceData}
        />
      )}
    </div>
  );
};

export default Attendance;